import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../../themes/chartTheme';
import {
  Box,
  useTheme,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { tokens } from '../../theme';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import { exportToExcel } from '../../helpers/exportFuncs';
import LoadingSpinner from '../LoadingSpinner';
import moment from 'moment';

interface IAnfoPerNewsletterData {
  date: string;
  newsletter: string;
  anfo_30d: number;
}

interface IStreamData {
  [key: string]: number | string;
}

const AnfoPerNewsletter: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const [chartData, setChartData] = useState<IStreamData[]>([]);
  const [loading, setLoading] = useState(false);
  const [availableNewsletters, setAvailableNewsletters] = useState<string[]>([]);
  const [selectedNewsletters, setSelectedNewsletters] = useState<string[]>([]);

  const fetchAnfoData = async (filterState: IFilterState): Promise<IAnfoPerNewsletterData[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<IAnfoPerNewsletterData>('/api/vnr/anfo-per-newsletter', params) as Promise<
      IAnfoPerNewsletterData[]
    >;
  };

  const determineAggregation = (
    startDate: Date,
    endDate: Date,
    userSelection: string,
  ): 'daily' | 'weekly' | 'monthly' => {
    if (userSelection !== 'auto') {
      return userSelection as 'daily' | 'weekly' | 'monthly';
    }

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 120) return 'monthly';
    if (diffDays > 45) return 'weekly';
    return 'daily';
  };

  const aggregateData = (
    data: IAnfoPerNewsletterData[],
    aggregationType: 'daily' | 'weekly' | 'monthly',
  ): IAnfoPerNewsletterData[] => {
    if (aggregationType === 'daily') return data;

    const aggregated: { [key: string]: IAnfoPerNewsletterData } = {};

    data.forEach((item) => {
      let key: string;
      const date = moment(item.date);

      if (aggregationType === 'weekly') {
        key = `${date.year()}-W${date.isoWeek().toString().padStart(2, '0')}`;
      } else {
        key = date.format('YYYY-MM');
      }

      const aggregationKey = `${key}_${item.newsletter}`;

      if (!aggregated[aggregationKey]) {
        aggregated[aggregationKey] = {
          date: key,
          newsletter: item.newsletter,
          anfo_30d: 0,
        };
      }

      aggregated[aggregationKey].anfo_30d += parseFloat(String(item.anfo_30d)) || 0;
    });

    return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));
  };

  useEffect(() => {
    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork || !filterState.startDate || !filterState.endDate) {
        return;
      }

      setLoading(true);
      try {
        const rawData = await fetchAnfoData(filterState);

        // Extract unique newsletters
        const newsletterSet = new Set(rawData.map((item) => item.newsletter));
        const newsletters = Array.from(newsletterSet).sort();
        setAvailableNewsletters(newsletters);

        // Initialize with all newsletters selected if none are selected
        if (selectedNewsletters.length === 0) {
          setSelectedNewsletters(newsletters);
        }

        // Determine aggregation based on date range and user selection
        const aggregationType = determineAggregation(
          filterState.startDate,
          filterState.endDate,
          filterState.vnrAggregation,
        );
        const aggregatedData = aggregateData(rawData, aggregationType);

        // Filter data based on selected newsletters
        const filteredData = aggregatedData.filter(
          (item) =>
            selectedNewsletters.length === 0 || selectedNewsletters.includes(item.newsletter),
        );

        // Group by date for chart
        const groupedByDate: { [key: string]: IStreamData } = {};

        filteredData.forEach((item) => {
          if (!groupedByDate[item.date]) {
            groupedByDate[item.date] = { date: item.date };
          }
          groupedByDate[item.date][item.newsletter] = parseFloat(String(item.anfo_30d)) || 0;
        });

        const processedData = Object.values(groupedByDate).sort((a, b) =>
          String(a.date).localeCompare(String(b.date)),
        );

        setChartData(processedData);
      } catch (error) {
        console.error('Error fetching ANFO per newsletter data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
    filterState.startDate,
    filterState.endDate,
    filterState.vnrAggregation,
    selectedNewsletters,
  ]);

  const handleNewsletterChange = (event: any) => {
    const value = event.target.value;
    setSelectedNewsletters(typeof value === 'string' ? value.split(',') : value);
  };

  const handleExport = () => {
    const exportData = chartData.map((item) => {
      const row: any = { Date: item.date };
      selectedNewsletters.forEach((newsletter) => {
        row[newsletter] = Number(item[newsletter]) || 0;
      });
      return row;
    });
    exportToExcel(exportData, 'ANFO_Per_Newsletter');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (chartData.length === 0) {
    return (
      <Box sx={{ margin: 2, textAlign: 'center', color: 'text.secondary' }}>
        <p>No ANFO data available for the selected period and filters.</p>
      </Box>
    );
  }

  // Prepare data for line chart
  const lineData = selectedNewsletters.map((newsletter, index) => {
    const colors = [
      '#1f77b4',
      '#ff7f0e',
      '#2ca02c',
      '#d62728',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf',
    ];
    return {
      id: newsletter,
      color: colors[index % colors.length],
      data: chartData.map((item) => ({
        x: item.date,
        y: Number(item[newsletter]) || 0,
      })),
    };
  });

  return (
    <Box sx={{ margin: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <h2 style={{ margin: 0 }}>Anfos pro NL (ohne Tripwire)</h2>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id="newsletter-select-label">Newsletter</InputLabel>
            <Select
              labelId="newsletter-select-label"
              id="newsletter-select"
              multiple
              value={selectedNewsletters}
              onChange={handleNewsletterChange}
              input={<OutlinedInput id="select-multiple-newsletter-chip" label="Newsletter" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {availableNewsletters.map((newsletter) => (
                <MenuItem key={newsletter} value={newsletter}>
                  {newsletter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={handleExport}>
            Export to Excel
          </Button>
        </Box>
      </Box>
      <Box sx={{ height: 400 }}>
        <ResponsiveLine
          data={lineData}
          margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Time Period',
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'ANFOs (30d)',
            legendOffset: -40,
            legendPosition: 'middle',
          }}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          lineWidth={3}
          theme={chartTheme}
          enableGridX={false}
          enableGridY={true}
          useMesh={true}
          tooltip={({ point }) => {
            const value = parseFloat(point.data.yFormatted as string);
            const displayValue = value.toLocaleString('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });

            return (
              <Box
                sx={{
                  padding: '8px 12px',
                  background: colors.primary[400],
                  color: colors.grey[100],
                  borderRadius: 1,
                  boxShadow: 2,
                }}
              >
                <strong>{point.serieId}</strong>: {displayValue}
                <br />
                <small>{`Period: ${point.data.x}`}</small>
              </Box>
            );
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 100,
              itemWidth: 80,
              itemHeight: 20,
              itemTextColor: colors.grey[100],
              symbolSize: 12,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: colors.grey[900],
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default AnfoPerNewsletter;
